import React from "react";
import api from "@/lib/api";
import { fmtCurrency } from "@/components/dash/ui";
import { StatusBadge } from "@/components/StatusBadge";
import { format } from "date-fns";

const d = (v) => { try { return v ? format(new Date(v), "dd MMM yyyy") : "—"; } catch { return "—"; } };
const money = (v) => fmtCurrency(v);

// helper to build status transition actions
const trans = (patches) => (row) => patches.filter((p) => p.from.includes(row.status)).map((p) => ({ label: p.label, patch: { status: p.to }, toast: p.toast, danger: p.danger }));

export const RESOURCES = {
  requirements: {
    key: "requirements", title: "Requirements", description: "Business and staffing requirements",
    endpoint: "/requirements", searchPlaceholder: "Search requirements…",
    statuses: ["draft", "active", "closed"], emptyIcon: "ClipboardList", emptyTitle: "No requirements yet",
    emptyDesc: "Create your first requirement to start sourcing talent or services.",
    columns: [
      { label: "Title", key: "title", render: (r) => <span className="font-medium text-slate-800">{r.title}</span> },
      { label: "Type", render: (r) => <span className="capitalize text-slate-600">{r.type}</span> },
      { label: "Client", key: "client_name" },
      { label: "Openings", key: "openings" },
      { label: "Location", key: "location" },
      { label: "Status", key: "status", status: true },
    ],
    detailFields: ["title", "type", "client_name", "department", "openings", "skills", "experience", "education", "location", "work_mode", "salary", "budget", "timeline", "employment_type", "jd", "status"],
    actions: (row, role) => {
      const a = [];
      if (role === "client" || role === "super_admin") {
        if (row.status === "draft") a.push({ label: "Activate", patch: { status: "active" }, toast: "Requirement activated" });
        if (row.status === "active") a.push({ label: "Close", patch: { status: "closed" }, toast: "Requirement closed", danger: true });
      }
      return a;
    },
  },
  rfqs: {
    key: "rfqs", title: "RFQs / RFPs", description: "Requests for quotation",
    endpoint: "/rfqs", searchPlaceholder: "Search RFQs…", statuses: ["open", "under_review", "awarded", "closed"],
    emptyIcon: "FileText", emptyTitle: "No RFQs yet", emptyDesc: "Create an RFQ and invite vendors to submit quotations.",
    columns: [
      { label: "Title", key: "title", render: (r) => <span className="font-medium text-slate-800">{r.title}</span> },
      { label: "Client", key: "client_name" },
      { label: "Quotes", render: (r) => r.quotation_count ?? 0 },
      { label: "Deadline", render: (r) => d(r.deadline) },
      { label: "Status", key: "status", status: true },
    ],
    detailFields: ["title", "scope", "client_name", "deadline", "status"],
    create: {
      label: "New RFQ", roles: ["client", "super_admin"], endpoint: "/rfqs",
      fields: [
        { name: "title", label: "RFQ Title", required: true },
        { name: "scope", label: "Scope of Work", type: "textarea" },
        { name: "deadline", label: "Submission Deadline", type: "date" },
      ],
    },
    actions: (row, role) => (role === "client" || role === "super_admin") ? trans([
      { from: ["open"], to: "under_review", label: "Move to Review", toast: "RFQ under review" },
      { from: ["open", "under_review"], to: "closed", label: "Close RFQ", danger: true },
    ])(row) : [],
  },
  quotations: {
    key: "quotations", title: "Quotations", description: "Vendor quotations",
    endpoint: "/quotations", searchPlaceholder: "Search quotations…",
    statuses: ["submitted", "under_review", "clarification", "shortlisted", "awarded", "rejected", "expired"],
    emptyIcon: "Quote", emptyTitle: "No quotations yet", emptyDesc: "Submitted quotations will appear here.",
    columns: [
      { label: "Title", key: "title", render: (r) => <span className="font-medium text-slate-800">{r.title}</span> },
      { label: "Vendor", key: "vendor_name" },
      { label: "Total", render: (r) => <span className="font-mono">{money(r.total)}</span> },
      { label: "Validity", render: (r) => d(r.validity) },
      { label: "Status", key: "status", status: true },
    ],
    detailFields: ["title", "vendor_name", "rfq_title", { key: "subtotal", label: "Subtotal", render: (r) => money(r.subtotal) }, { key: "tax", label: "Tax %" }, { key: "total", label: "Total", render: (r) => money(r.total) }, "delivery_timeline", "validity", "notes", "status"],
    create: {
      label: "Submit Quotation", roles: ["vendor"], endpoint: "/quotations", title: "Submit Quotation",
      fields: [
        { name: "rfq_id", label: "RFQ", type: "select-async", endpoint: "/rfqs", optionLabel: (r) => r.title, required: true },
        { name: "title", label: "Proposal Title" },
        { name: "amount", label: "Quote Amount (₹)", type: "number", required: true },
        { name: "tax", label: "Tax %", type: "number", default: 18 },
        { name: "delivery_timeline", label: "Delivery Timeline", placeholder: "e.g. 8 weeks" },
        { name: "validity", label: "Valid Until", type: "date" },
        { name: "notes", label: "Notes", type: "textarea" },
      ],
      transform: (p) => ({ line_items: [{ description: p.title || "Services", qty: 1, rate: Number(p.amount || 0) }] }),
    },
    actions: (row, role) => {
      if (role === "client" || role === "super_admin") return trans([
        { from: ["submitted"], to: "under_review", label: "Mark Under Review" },
        { from: ["submitted", "under_review"], to: "clarification", label: "Request Clarification" },
        { from: ["submitted", "under_review", "clarification"], to: "shortlisted", label: "Shortlist" },
        { from: ["shortlisted", "under_review"], to: "awarded", label: "Award", toast: "Quotation awarded" },
        { from: ["submitted", "under_review", "shortlisted", "clarification"], to: "rejected", label: "Reject", danger: true },
      ])(row);
      return [];
    },
  },
  candidates: {
    key: "candidates", title: "Candidate Database", description: "Your talent pool",
    endpoint: "/candidates", searchPlaceholder: "Search candidates…",
    statuses: ["sourced", "screened", "submitted", "shortlisted", "interview", "selected", "offer", "bgv", "joined", "deployed", "rejected", "withdrawn"],
    emptyIcon: "Users", emptyTitle: "No candidates yet", emptyDesc: "Add candidates to start building your talent pool.",
    columns: [
      { label: "Name", key: "name", render: (r) => <span className="font-medium text-slate-800">{r.name}</span> },
      { label: "Skills", render: (r) => <span className="text-slate-600">{(r.skills || []).slice(0, 3).join(", ")}</span> },
      { label: "Experience", key: "experience" },
      { label: "Location", key: "current_location" },
      { label: "Status", key: "status", status: true },
    ],
    detailFields: ["name", "email", "phone", "skills", "experience", "education", "current_location", "preferred_location", "expected_salary", "notice_period", "availability", "status"],
    create: {
      label: "Add Candidate", roles: ["staffing"], endpoint: "/candidates",
      fields: [
        { name: "name", label: "Full Name", required: true },
        { name: "email", label: "Email" },
        { name: "phone", label: "Phone" },
        { name: "skills", label: "Skills", type: "tags", hint: "Comma separated" },
        { name: "experience", label: "Experience", placeholder: "e.g. 5 years" },
        { name: "education", label: "Education" },
        { name: "current_location", label: "Current Location" },
        { name: "preferred_location", label: "Preferred Location" },
        { name: "expected_salary", label: "Expected Salary" },
        { name: "notice_period", label: "Notice Period" },
        { name: "availability", label: "Availability", type: "select", options: ["Available", "1 Month", "2 Months", "Not Available"], default: "Available" },
      ],
    },
    actions: (row, role) => role === "staffing" ? [
      { label: "Mark Screened", patch: { status: "screened" } },
    ] : [],
  },
  submissions: {
    key: "submissions", title: "Candidate Submissions", description: "Candidates submitted to requirements",
    endpoint: "/submissions", searchPlaceholder: "Search submissions…",
    statuses: ["submitted", "shortlisted", "interview", "selected", "offer", "bgv", "joined", "deployed", "rejected", "withdrawn"],
    emptyIcon: "UserPlus", emptyTitle: "No submissions yet", emptyDesc: "Candidate submissions will appear here.",
    columns: [
      { label: "Candidate", key: "candidate_name", render: (r) => <span className="font-medium text-slate-800">{r.candidate_name}</span> },
      { label: "Position", key: "position" },
      { label: "Status", key: "status", status: true },
    ],
    detailFields: ["candidate_name", "position", "expected_salary", "status"],
    create: {
      label: "Submit Candidate", roles: ["staffing"], endpoint: "/submissions", title: "Submit Candidate to Requirement",
      fields: [
        { name: "candidate_id", label: "Candidate", type: "select-async", endpoint: "/candidates", optionLabel: (c) => `${c.name} · ${(c.skills || []).slice(0, 2).join(", ")}`, required: true },
        { name: "requirement_id", label: "Requirement", type: "select-async", endpoint: "/requirements", optionLabel: (r) => `${r.title} (${r.client_name || ""})`, required: true },
      ],
    },
    actions: (row, role) => {
      if (role === "client" || role === "super_admin") return trans([
        { from: ["submitted"], to: "shortlisted", label: "Shortlist", toast: "Candidate shortlisted" },
        { from: ["interview"], to: "selected", label: "Mark Selected", toast: "Candidate selected" },
        { from: ["submitted", "shortlisted", "interview"], to: "rejected", label: "Reject", danger: true },
      ])(row);
      if (role === "staffing") return trans([
        { from: ["selected"], to: "offer", label: "Roll Out Offer" },
        { from: ["offer"], to: "bgv", label: "Start BGV" },
        { from: ["bgv"], to: "joined", label: "Mark Joined" },
        { from: ["joined"], to: "deployed", label: "Mark Deployed" },
      ])(row);
      return [];
    },
  },
  interviews: {
    key: "interviews", title: "Interviews", description: "Scheduled interviews",
    endpoint: "/interviews", searchPlaceholder: "Search interviews…", statuses: ["scheduled", "completed", "cancelled"],
    emptyIcon: "CalendarCheck", emptyTitle: "No interviews yet", emptyDesc: "Scheduled interviews will appear here.",
    columns: [
      { label: "Candidate", key: "candidate_name", render: (r) => <span className="font-medium text-slate-800">{r.candidate_name}</span> },
      { label: "Position", key: "position" },
      { label: "Round", key: "round" },
      { label: "Mode", key: "mode" },
      { label: "When", render: (r) => d(r.scheduled_at) },
      { label: "Status", key: "status", status: true },
    ],
    detailFields: ["candidate_name", "position", "round", "mode", "scheduled_at", "status", "result", "feedback"],
    create: {
      label: "Schedule Interview", roles: ["client", "staffing", "super_admin"], endpoint: "/interviews", title: "Schedule Interview",
      fields: [
        { name: "submission_id", label: "Submission", type: "select-async", endpoint: "/submissions", optionLabel: (s) => `${s.candidate_name} · ${s.position}`, required: true },
        { name: "round", label: "Round", type: "select", options: ["Round 1", "Round 2", "Technical", "Final"], default: "Round 1" },
        { name: "mode", label: "Mode", type: "select", options: ["Video", "Phone", "Onsite"], default: "Video" },
        { name: "scheduled_at", label: "Date", type: "date" },
      ],
    },
    actions: (row) => row.status === "scheduled" ? [
      { label: "Mark Completed · Selected", patch: { status: "completed", result: "selected" }, toast: "Interview completed" },
      { label: "Mark Completed · Rejected", patch: { status: "completed", result: "rejected" }, danger: true },
    ] : [],
  },
  selections: {
    key: "selections", title: "Selections", description: "Selected & deployed candidates",
    endpoint: "/submissions", params: { status: "selected" }, searchPlaceholder: "Search…",
    emptyIcon: "Award", emptyTitle: "No selections yet", emptyDesc: "Selected candidates will appear here.",
    columns: [
      { label: "Candidate", key: "candidate_name", render: (r) => <span className="font-medium text-slate-800">{r.candidate_name}</span> },
      { label: "Position", key: "position" },
      { label: "Status", key: "status", status: true },
    ],
    detailFields: ["candidate_name", "position", "status"],
  },
  "purchase-orders": {
    key: "purchase-orders", title: "Purchase Orders", description: "Issued purchase orders",
    endpoint: "/purchase-orders", searchPlaceholder: "Search POs…", statuses: ["issued", "acknowledged", "completed"],
    emptyIcon: "ShoppingCart", emptyTitle: "No purchase orders", emptyDesc: "Award a quotation to generate a purchase order.",
    columns: [
      { label: "PO Number", key: "po_number", render: (r) => <span className="font-mono font-medium text-slate-800">{r.po_number}</span> },
      { label: "Vendor", key: "vendor_name" },
      { label: "Client", key: "client_name" },
      { label: "Total", render: (r) => <span className="font-mono">{money(r.total)}</span> },
      { label: "Status", key: "status", status: true },
    ],
    detailFields: ["po_number", "vendor_name", "client_name", { key: "total", label: "Total", render: (r) => money(r.total) }, "delivery_terms", "payment_terms", "validity", "status"],
    create: {
      label: "Create PO", roles: ["client", "super_admin"], endpoint: "/purchase-orders", title: "Create Purchase Order",
      fields: [
        { name: "quotation_id", label: "From Awarded Quotation", type: "select-async", endpoint: "/quotations", optionLabel: (q) => `${q.title} · ${money(q.total)}` },
        { name: "delivery_terms", label: "Delivery Terms", placeholder: "Net 45 days" },
        { name: "payment_terms", label: "Payment Terms", placeholder: "Milestone based" },
        { name: "validity", label: "Valid Until", type: "date" },
      ],
    },
    actions: (row, role) => {
      if (role === "vendor" && row.status === "issued") return [{ label: "Acknowledge PO", patch: { status: "acknowledged" }, toast: "PO acknowledged" }];
      if ((role === "client" || role === "super_admin") && row.status === "acknowledged") return [{ label: "Mark Completed", patch: { status: "completed" } }];
      return [];
    },
  },
  contracts: {
    key: "contracts", title: "Contracts", description: "Contract lifecycle management",
    endpoint: "/contracts", searchPlaceholder: "Search contracts…", statuses: ["draft", "active", "expiring", "expired", "renewed", "terminated"],
    emptyIcon: "FileSignature", emptyTitle: "No contracts yet", emptyDesc: "Create a contract to track value, terms and renewals.",
    columns: [
      { label: "Title", key: "title", render: (r) => <span className="font-medium text-slate-800">{r.title}</span> },
      { label: "Counterparty", key: "counterparty_name" },
      { label: "Value", render: (r) => <span className="font-mono">{money(r.value)}</span> },
      { label: "End Date", render: (r) => d(r.end_date) },
      { label: "Status", key: "status", status: true },
    ],
    detailFields: ["title", "client_name", "counterparty_name", { key: "value", label: "Value", render: (r) => money(r.value) }, "start_date", "end_date", "renewal_date", "status"],
    create: {
      label: "New Contract", roles: ["client", "super_admin"], endpoint: "/contracts",
      fields: [
        { name: "title", label: "Contract Title", required: true },
        { name: "counterparty_org_id", label: "Counterparty", type: "select-async", endpoint: "/organizations", optionLabel: (o) => `${o.name} (${o.type})` },
        { name: "value", label: "Contract Value (₹)", type: "number" },
        { name: "start_date", label: "Start Date", type: "date" },
        { name: "end_date", label: "End Date", type: "date" },
        { name: "renewal_date", label: "Renewal Date", type: "date" },
      ],
    },
    actions: (row, role) => (role === "client" || role === "super_admin") ? trans([
      { from: ["active", "expiring"], to: "renewed", label: "Mark Renewed" },
      { from: ["draft"], to: "active", label: "Activate" },
      { from: ["active", "expiring", "draft"], to: "terminated", label: "Terminate", danger: true },
    ])(row) : [],
  },
  invoices: {
    key: "invoices", title: "Invoices", description: "Invoice management",
    endpoint: "/invoices", searchPlaceholder: "Search invoices…",
    statuses: ["draft", "submitted", "under_review", "approved", "partially_paid", "paid", "rejected", "overdue"],
    emptyIcon: "Receipt", emptyTitle: "No invoices yet", emptyDesc: "Create an invoice to bill for delivered work.",
    columns: [
      { label: "Invoice", key: "invoice_number", render: (r) => <span className="font-mono font-medium text-slate-800">{r.invoice_number}</span> },
      { label: "Issuer", key: "issuer_name" },
      { label: "Client", key: "client_name" },
      { label: "Total", render: (r) => <span className="font-mono">{money(r.total)}</span> },
      { label: "Due", render: (r) => d(r.due_date) },
      { label: "Status", key: "status", status: true },
    ],
    detailFields: ["invoice_number", "issuer_name", "client_name", { key: "amount", label: "Amount", render: (r) => money(r.amount) }, { key: "tax", label: "Tax %" }, { key: "total", label: "Total", render: (r) => money(r.total) }, { key: "paid_amount", label: "Paid", render: (r) => money(r.paid_amount) }, "due_date", "status"],
    create: {
      label: "New Invoice", roles: ["vendor", "staffing", "super_admin"], endpoint: "/invoices",
      fields: [
        { name: "client_org_id", label: "Bill To (Client)", type: "select-async", endpoint: "/organizations", optionLabel: (o) => o.name, required: true },
        { name: "amount", label: "Amount (₹)", type: "number", required: true },
        { name: "tax", label: "Tax %", type: "number", default: 18 },
        { name: "due_date", label: "Due Date", type: "date" },
      ],
    },
    actions: (row, role) => {
      if (role === "client" || role === "super_admin") {
        const a = trans([
          { from: ["submitted"], to: "under_review", label: "Mark Under Review" },
          { from: ["submitted", "under_review"], to: "approved", label: "Approve", toast: "Invoice approved" },
          { from: ["submitted", "under_review"], to: "rejected", label: "Reject", danger: true },
        ])(row);
        if (row.status === "approved" || row.status === "partially_paid") a.push({ label: "Record Payment", toast: "Payment recorded", custom: async (r) => api.post("/payments", { invoice_id: r.id, amount: r.total - (r.paid_amount || 0) }) });
        return a;
      }
      return [];
    },
  },
  payments: {
    key: "payments", title: "Payments", description: "Payment records",
    endpoint: "/payments", searchPlaceholder: "Search payments…",
    emptyIcon: "CreditCard", emptyTitle: "No payments yet", emptyDesc: "Recorded payments will appear here.",
    columns: [
      { label: "Reference", key: "reference", render: (r) => <span className="font-mono text-slate-800">{r.reference}</span> },
      { label: "Invoice", key: "invoice_number" },
      { label: "Amount", render: (r) => <span className="font-mono">{money(r.amount)}</span> },
      { label: "Method", key: "method" },
      { label: "Status", key: "status", status: true },
    ],
    detailFields: ["reference", "invoice_number", { key: "amount", label: "Amount", render: (r) => money(r.amount) }, "method", "paid_at", "status"],
  },
  users: {
    key: "users", title: "Users", description: "Team members & platform users",
    endpoint: "/users", searchPlaceholder: "Search users…",
    emptyIcon: "Users", emptyTitle: "No users", emptyDesc: "Add team members to your organization.",
    columns: [
      { label: "Name", key: "name", render: (r) => <span className="font-medium text-slate-800">{r.name}</span> },
      { label: "Email", key: "email" },
      { label: "Role", render: (r) => <span className="capitalize text-slate-600">{r.role.replace("_", " ")}</span> },
      { label: "Title", key: "title" },
      { label: "Status", key: "status", status: true },
    ],
    detailFields: ["name", "email", "role", "title", "permissions", "status"],
    create: {
      label: "Add User", roles: ["client", "vendor", "staffing"], endpoint: "/users", title: "Add Team Member",
      fields: [
        { name: "name", label: "Name", required: true },
        { name: "email", label: "Email", required: true },
        { name: "password", label: "Temporary Password", required: true },
        { name: "title", label: "Title" },
      ],
    },
  },
  "audit-logs": {
    key: "audit-logs", title: "Audit Logs", description: "Platform action history",
    endpoint: "/audit-logs", searchPlaceholder: "Search audit trail…",
    emptyIcon: "ScrollText", emptyTitle: "No audit records", emptyDesc: "Important actions will be logged here.",
    columns: [
      { label: "Actor", key: "actor_name", render: (r) => <span className="font-medium text-slate-800">{r.actor_name}</span> },
      { label: "Action", key: "action", render: (r) => <span className="capitalize">{r.action}</span> },
      { label: "Entity", render: (r) => <span className="capitalize text-slate-600">{(r.entity_type || "").replace(/_/g, " ")}</span> },
      { label: "Change", render: (r) => r.prev_status || r.new_status ? <span className="text-xs text-slate-500">{r.prev_status || "—"} → {r.new_status || "—"}</span> : "—" },
      { label: "When", render: (r) => d(r.timestamp) },
    ],
    detailFields: ["actor_name", "action", "entity_type", "entity_id", "prev_status", "new_status", "ip", "timestamp"],
  },
};

// Organization-based pages
const ORG_COLUMNS = [
  { label: "Company", key: "name", render: (r) => <span className="font-medium text-slate-800">{r.name}</span> },
  { label: "Industry", key: "industry" },
  { label: "Location", key: "location" },
  { label: "Verification", key: "verification_status", status: true },
  { label: "Status", key: "status", status: true },
];
const orgDetail = ["name", "type", "industry", "location", "size", "services", "technologies", "website", "gst", "pan", "msme", "description", "verification_status", "status"];
const orgActions = (row, role) => {
  if (role !== "super_admin") return [];
  const a = [];
  if (row.verification_status !== "verified") a.push({ label: "Verify", toast: "Organization verified", custom: async (r) => api.post(`/organizations/${r.id}/verify`, { verification_status: "verified" }) });
  if (row.verification_status === "pending") a.push({ label: "Reject", danger: true, toast: "Organization rejected", custom: async (r) => api.post(`/organizations/${r.id}/verify`, { verification_status: "rejected" }) });
  if (row.status === "active") a.push({ label: "Suspend", danger: true, toast: "Suspended", custom: async (r) => api.post(`/organizations/${r.id}/status`, { status: "suspended" }) });
  else a.push({ label: "Activate", toast: "Activated", custom: async (r) => api.post(`/organizations/${r.id}/status`, { status: "active" }) });
  return a;
};

const orgCfg = (key, title, params, opts = {}) => ({
  key, title, description: opts.description || title, endpoint: "/organizations", params,
  searchPlaceholder: "Search companies…", statuses: opts.statuses,
  emptyIcon: "Building2", emptyTitle: `No ${title.toLowerCase()}`, emptyDesc: opts.emptyDesc,
  columns: ORG_COLUMNS, detailFields: orgDetail, actions: orgActions,
});

RESOURCES["clients"] = orgCfg("clients", "Clients", { type: "client" }, { description: "Enterprise client organizations" });
RESOURCES["vendors"] = orgCfg("vendors", "Vendors", { type: "vendor" }, { description: "Vendor partner organizations" });
RESOURCES["staffing-companies"] = orgCfg("staffing-companies", "Staffing Companies", { type: "staffing" }, { description: "Staffing partner organizations" });
RESOURCES["approvals"] = orgCfg("approvals", "Pending Approvals", { verification: "pending" }, { description: "Organizations awaiting verification", emptyDesc: "No pending approvals right now." });
RESOURCES["verification"] = orgCfg("verification", "Verification Queue", { verification: "pending" }, { description: "KYC & document verification queue" });
